import './LicenseProductsList.css';

import React, { useState, useEffect } from 'react';
import { TreeItem, TreeView } from '@mui/x-tree-view';
import { Typography, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon from '@mui/icons-material/Settings';
import Chip from '@mui/material/Chip';
import { AddCircle, Circle, RemoveCircle } from '@mui/icons-material';
import { generateClassName, promptForNumericValue } from '../../../b2b-common/helpers';

export const netProductsAcronyms = (productsData) => {
    const netProductsAcronymsResult = [];
    const { includedProducts, newlyAddedProducts, toRemoveProducts } = productsData;
    const updatedProducts = includedProducts ? includedProducts.concat(newlyAddedProducts) : newlyAddedProducts;
    updatedProducts.forEach((product) => {
        if (!netProductsAcronymsResult.includes(product.productAcronym)) {
            netProductsAcronymsResult.push(product.productAcronym);
        }
    });
    toRemoveProducts.forEach((product) => {
        if (netProductsAcronymsResult.includes(product.productAcronym)) {
            netProductsAcronymsResult.splice(netProductsAcronymsResult.indexOf(product.productAcronym), 1);
        }
    });
    return netProductsAcronymsResult;
};

export const netProducts = (productsData) => {
    const netProductsResult = [];
    const { includedProducts, newlyAddedProducts, toRemoveProducts } = productsData;
    const updatedProducts = includedProducts ? includedProducts.concat(newlyAddedProducts) : newlyAddedProducts;
    updatedProducts.forEach((product) => {
        if (!netProductsResult.some((p) => p.productId === product.productId)) {
            netProductsResult.push(product);
        }
    });
    toRemoveProducts.forEach((product) => {
        if (netProductsResult.some((p) => p.productId === product.productId)) {
            netProductsResult.splice(netProductsResult.findIndex((p) => p.productId === product.productId), 1);
        }
    });
    return netProductsResult;
};

const LicenseProductsList = ({ productsData, includedProducts = [], isFetching, onProductsChange, title = 'Products in this license', className = null }) => {
    const [expanded, setExpanded] = useState([]);
    const [newlyAddedProducts, setNewlyAddedProducts] = useState([]);
    const [toRemoveProducts, setToRemoveProducts] = useState([]);
    const [updatedProducts, setUpdatedProducts] = useState(includedProducts);

    useEffect(() => {
        onProductsChange({
            includedProducts,
            newlyAddedProducts,
            toRemoveProducts,
        });
        setUpdatedProducts(calculateUpdatedProducts());
    }, [newlyAddedProducts, toRemoveProducts]);

    useEffect(() => {
        // Automatically expand the first node when data is available
        if (productsData && productsData.length > 0) {
            setExpanded([productsData[0].productId.toString()]);
        }
    }, [productsData]);

    useEffect(() => {
        setUpdatedProducts(calculateUpdatedProducts());
    }, [includedProducts]);

    const calculateUpdatedProducts = () => {
        const updatedProductsList = includedProducts
            .filter(product => !toRemoveProducts.some(removeProduct => removeProduct.productId === product.productId))
            .concat(newlyAddedProducts);
        return updatedProductsList;
    };

    const handleDoubleClick = (node) => {
        // Check if the product is not in the includedProducts list
        if (!includedProducts.some((product) => product.productId === node.productId)) {
            if (newlyAddedProducts.some((product) => product.productId === node.productId)) {
                // Product was previously added, remove it
                setNewlyAddedProducts((prevProducts) =>
                    prevProducts.filter((product) => product.productId !== node.productId)
                );
            } else {
                // Product was not added, add it
                if (node.productParameterized) {
                    const parameterValue = promptForNumericValue(`Enter the value for ${node.productName}:`);
                    if (parameterValue !== null) {
                        const nodeWithParameter = { ...node, parameterValue };
                        setNewlyAddedProducts((prevProducts) => [...prevProducts, nodeWithParameter]);
                    } else {
                        console.log('Parameter value is null');
                    }
                } else {
                    setNewlyAddedProducts((prevProducts) => [...prevProducts, node]);
                }
            }
        } else {
            if (toRemoveProducts.some((product) => product.productId === node.productId)) {
                // Product was previously added, remove it
                setToRemoveProducts((prevProducts) =>
                    prevProducts.filter((product) => product.productId !== node.productId)
                );
            } else {
                // Product was already included, remove it
                setToRemoveProducts((prevProducts) => [...prevProducts, node]);
            }
        }
    };

    const renderTree = (node) => {

        if (!node || !node.productId) {
            return null; // Add a check for undefined node or missing productId
        }

        const isBold = Array.isArray(node.childProducts) && node.childProducts.length > 0;
        const isProductParameterized = node.productParameterized;
        const valueOfParameter = updatedProducts.find(product => product.productId === node.productId)?.parameterValue;
        const isProductIncluded = includedProducts.some(product => product.productId === node.productId);
        const isProductToBeIncluded = newlyAddedProducts.some(product => product.productId === node.productId);
        const isProductToBeRemoved = toRemoveProducts.some(product => product.productId === node.productId);

        // Sort child products
        const sortedChildProducts = Array.isArray(node.childProducts)
            ? [
                ...node.childProducts.filter(
                    (child) => !child.childProducts || child.childProducts.length === 0
                ),
                ...node.childProducts.filter((child) => child.childProducts && child.childProducts.length > 0),
            ]
            : [];

        return (
            <TreeItem
                key={node.productId.toString() + (valueOfParameter !== undefined ? valueOfParameter : '')}
                nodeId={node.productId.toString()}
                label={
                    <>
                        <div className={`tree-item-label ${(isProductIncluded || isProductToBeIncluded) ? 'included-product' : ''}`}
                            onDoubleClick={() => handleDoubleClick(node)}
                        >
                            <Typography color={node.productEnabled ? 'inherit' : 'error'} className={`center-vertically ${isBold ? 'bold-text' : ''}`}>
                                {isProductParameterized && <SettingsIcon className="ProductParameterizedIcon" />} {node.productName}
                                {(isProductIncluded && !isProductToBeRemoved) ? <Circle className="CircleIcon" /> : ''}
                                {isProductToBeIncluded ? <AddCircle className="AddCircleIcon" /> : ''}
                                {isProductToBeRemoved ? <RemoveCircle className="RemoveCircleIcon" /> : ''}

                            </Typography>
                            {(isProductParameterized && valueOfParameter !== undefined) ?
                                (<div className="tree-item-parameter-value">{valueOfParameter}</div>) : ''}
                        </div>
                    </>
                }
            >
                {sortedChildProducts.map(renderTree)}
            </TreeItem>
        );
    };

    // Counters
    const numIncludedProducts = includedProducts.length;
    const numNewlyAddedProducts = newlyAddedProducts.length;
    const numToRemoveProducts = toRemoveProducts.length;

    return (
        <>
            <div className={generateClassName('license-products-list','',className)}>
                {isFetching ? (
                    <CircularProgress />
                ) : (
                    <>
                        <div className="tree-view-header">
                            <Typography>{ title }</Typography>
                            <div className="counters">
                                <Typography color="inherit">{`Included: ${numIncludedProducts}`}</Typography>
                                <Typography color="primary">{`To be Added: ${numNewlyAddedProducts}`}</Typography>
                                <Typography color="error">{`To be Removed: ${numToRemoveProducts}`}</Typography>
                            </div>
                        </div>
                        <TreeView
                            defaultCollapseIcon={<ExpandMoreIcon />}
                            defaultExpandIcon={<ChevronRightIcon />}
                            expanded={expanded}
                            onNodeToggle={(event, nodes) => setExpanded(nodes)}
                            className="tree-view-component"
                        >
                            {productsData ? (renderTree(productsData[0])) : ''}
                        </TreeView>
                        <div className="tree-view-footer">
                            {updatedProducts.length > 0 && (
                                <div className="chips-container">
                                    {updatedProducts.map((product) => {
                                        // console.log('Product ID:', product.productId);
                                        return (
                                            <Chip 
                                                key={product.productId} 
                                                label={product.productParameterized ? `${product.productAcronym}:${product.parameterValue}` : product.productAcronym} 
                                                size='small' 
                                                variant="outlined" 
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>

                )}
            </div>
        </>
    );
};

export default LicenseProductsList;